import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { ScriptableContext } from 'chart.js';
import { colorPalette } from 'utils/styleConstants';

type Value = { x: string; y: number; formattedY: string };

export interface HistogramChartProps {
  data: Value[];
  percentage?: boolean;
}

const HistogramChart = ({ data: dataValues, percentage }: HistogramChartProps) => {
  const chartRef = useRef<any>(null);

  const barWidth = 10;

  const createGradient = (context: ScriptableContext<'bar'>) => {
    const ctx = context.chart.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.clientHeight);
    gradient.addColorStop(0, colorPalette.turquoise.dark);
    gradient.addColorStop(1, colorPalette.turquoise.normal);
    return gradient;
  };

  const createOffsetData = (input: Value[]) => {
    const valMax = Math.round(Math.max(...input.map((e) => Number(e.y))));
    return input.map((item: Value) => ({
      x: item.x,
      y: [item.y, valMax],
    }));
  };

  const createOffsetDataPercentage = (input: Value[]) => {
    return input.map((item: Value) => ({
      x: item.x,
      y: [item.y, 100],
    }));
  };

  const data = {
    datasets: [
      {
        data: dataValues,
        backgroundColor: (context: ScriptableContext<'bar'>) => createGradient(context),
        barThickness: barWidth,
        borderRadius: 3,
      },
      {
        label: '',
        data: percentage ? createOffsetDataPercentage(dataValues) : createOffsetData(dataValues),
        backgroundColor: colorPalette.turquoise.light,
        hoverBackgroundColor: colorPalette.turquoise.light,
        borderWidth: 2,
        borderColor: 'transparent',
        barThickness: barWidth + 4,
        borderRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    borderSkipped: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'Roboto',
          },
        },
      },
      y: {
        max: percentage ? 100 : null,
        ticks: {
          callback: (value: number | string) => {
            return percentage ? value + ' %' : value;
          },
          font: {
            size: 9,
            family: 'Roboto',
            weight: 'lighter',
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        filter: (tooltipItem: { datasetIndex: number }) => [0].includes(tooltipItem.datasetIndex),
        callbacks: {
          title: () => '',
          label: (tooltipItem: { raw: { formattedY: string } }) => {
            const value = tooltipItem.raw.formattedY;
            return percentage ? value + ' %' : value;
          },
        },
        backgroundColor: colorPalette.white,
        titleColor: colorPalette.turquoise.dark,
        bodyColor: colorPalette.turquoise.dark,
        displayColors: false,
        bodyFont: {
          size: 12,
          weight: 400,
        },
        caretPadding: 10,
        borderColor: colorPalette.grey.grey3,
        borderWidth: 1,
        padding: 7,
      },
    },
  };

  return (
    // @ts-ignore
    <Bar ref={chartRef} data={data} options={options} />
  );
};

export default HistogramChart;
